/**
 * useSharpSports Hook
 * Manages SharpSports integration state and actions
 */

import { useAuth } from '@contexts/AuthContext';
import { getUserFriendlyError } from '@utils/api';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface LinkedAccount {
  id: number;
  sportsbook: string;
  sportsbookName: string;
  status: 'verified' | 'unverified' | 'inaccessible';
  linkedAt: string;
  lastRefreshAt: string | null;
}

export interface ImportedBet {
  id: string;
  sportsbook: string;
  betType: 'single' | 'parlay';
  status: string;
  outcome: string | null;
  oddsAmerican: number | null;
  atRisk: number | null;
  toWin: number | null;
  netProfit: number | null;
  timePlaced: string | null;
  timeClosed: string | null;
  processed: boolean;
  parlayId: number | null;
}

export interface SharpSportsStatus {
  enabled: boolean;
  hasLinkedAccounts: boolean;
  linkedAccounts: { book: string; verified: boolean }[];
}

export interface BrowserExtensionStatus {
  isInstalled: boolean;
  version: string | null;
  authToken: string | null;
}

export interface UseSharpSportsResult {
  // Status
  status: SharpSportsStatus | null;
  linkedAccounts: LinkedAccount[];
  importedBets: ImportedBet[];

  // Browser extension status (for FanDuel, DraftKings, etc.)
  browserExtension: BrowserExtensionStatus;

  // Loading states
  loading: boolean;
  linkingInProgress: boolean;
  refreshingBets: boolean;
  processingBets: boolean;
  reconcilingBets: boolean;

  // Error state
  error: string | null;

  // Actions
  checkStatus: () => Promise<void>;
  linkAccount: (redirectUrl?: string) => Promise<string | null>;
  getLinkedAccounts: () => Promise<void>;
  getImportedBets: (limit?: number) => Promise<void>;
  refreshBets: () => Promise<{ success: boolean; message: string }>;
  processBets: () => Promise<{ processed: number; failed: number }>;
  reconcileBets: () => Promise<{ matched: number; unmatched: number }>;
  getBrowserExtensionAuthToken: () => Promise<string | null>;
  checkBrowserExtension: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useSharpSports(): UseSharpSportsResult {
  const { isAuthenticated, authFetch } = useAuth();

  // State
  const [status, setStatus] = useState<SharpSportsStatus | null>(null);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [importedBets, setImportedBets] = useState<ImportedBet[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [refreshingBets, setRefreshingBets] = useState(false);
  const [processingBets, setProcessingBets] = useState(false);
  const [reconcilingBets, setReconcilingBets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Browser extension state
  const [browserExtension, setBrowserExtension] = useState<BrowserExtensionStatus>({
    isInstalled: false,
    version: null,
    authToken: null,
  });

  // Check SharpSports status
  const checkStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus(null);
      return;
    }

    try {
      const res = await authFetch('/api/sharpsports/status');
      if (res.ok) {
        const data = (await res.json()) as SharpSportsStatus & {
          error?: string;
        };
        if (data.error) throw new Error(data.error);
        setStatus(data);
        setError(null);
      } else if (res.status === 403) {
        // Feature not enabled
        setStatus({
          enabled: false,
          hasLinkedAccounts: false,
          linkedAccounts: [],
        });
      } else {
        throw new Error('Failed to check SharpSports status');
      }
    } catch (err) {
      const message = getUserFriendlyError(err as Error);
      // Don't show error for disabled feature
      if (!message.includes('not enabled')) {
        setError(message);
      }
      setStatus({
        enabled: false,
        hasLinkedAccounts: false,
        linkedAccounts: [],
      });
    }
  }, [isAuthenticated, authFetch]);

  // Get linked accounts from our database
  const getLinkedAccounts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const res = await authFetch('/api/sharpsports/accounts');
      if (res.ok) {
        const data = (await res.json()) as {
          accounts: LinkedAccount[];
          error?: string;
        };
        if (data.error) throw new Error(data.error);
        setLinkedAccounts(data.accounts || []);
      }
    } catch (_err) {}
  }, [isAuthenticated, authFetch]);

  // Get imported bets
  const getImportedBets = useCallback(
    async (limit = 50) => {
      if (!isAuthenticated) return;

      try {
        const res = await authFetch(`/api/sharpsports/imported?limit=${limit}`);
        if (res.ok) {
          const data = (await res.json()) as {
            bets: ImportedBet[];
            error?: string;
          };
          if (data.error) throw new Error(data.error);
          setImportedBets(data.bets || []);
        }
      } catch (_err) {}
    },
    [isAuthenticated, authFetch],
  );

  // Create link context and return URL
  const linkAccount = useCallback(
    async (redirectUrl?: string): Promise<string | null> => {
      if (!isAuthenticated) return null;

      setLinkingInProgress(true);
      setError(null);

      try {
        const res = await authFetch('/api/sharpsports/link-context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            redirectUrl: redirectUrl || window.location.href,
          }),
        });

        if (res.ok) {
          const data = (await res.json()) as {
            linkUrl: string;
            cid: string;
            error?: string;
          };
          if (data.error) throw new Error(data.error);
          return data.linkUrl || null;
        } else {
          const data = (await res.json()) as { error?: string };
          throw new Error(data.error || 'Failed to create link context');
        }
      } catch (err) {
        setError(getUserFriendlyError(err as Error));
        return null;
      } finally {
        setLinkingInProgress(false);
      }
    },
    [isAuthenticated, authFetch],
  );

  // Refresh bets from sportsbooks
  const refreshBets = useCallback(async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    if (!isAuthenticated) return { success: false, message: 'Not authenticated' };

    setRefreshingBets(true);
    setError(null);

    try {
      const res = await authFetch('/api/sharpsports/refresh', {
        method: 'POST',
      });

      if (res.ok) {
        const data = (await res.json()) as {
          success: boolean;
          message: string;
          error?: string;
        };
        if (data.error) throw new Error(data.error);
        return { success: true, message: data.message || 'Refresh triggered' };
      } else {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error || 'Failed to refresh bets');
      }
    } catch (err) {
      const message = getUserFriendlyError(err as Error);
      setError(message);
      return { success: false, message };
    } finally {
      setRefreshingBets(false);
    }
  }, [isAuthenticated, authFetch]);

  // Process unprocessed bets
  const processBets = useCallback(async (): Promise<{
    processed: number;
    failed: number;
  }> => {
    if (!isAuthenticated) return { processed: 0, failed: 0 };

    setProcessingBets(true);
    setError(null);

    try {
      const res = await authFetch('/api/sharpsports/process', {
        method: 'POST',
      });

      if (res.ok) {
        const data = (await res.json()) as {
          processed: number;
          failed: number;
          error?: string;
        };
        if (data.error) throw new Error(data.error);
        // Refresh imported bets after processing
        await getImportedBets();
        return { processed: data.processed || 0, failed: data.failed || 0 };
      } else {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error || 'Failed to process bets');
      }
    } catch (err) {
      setError(getUserFriendlyError(err as Error));
      return { processed: 0, failed: 0 };
    } finally {
      setProcessingBets(false);
    }
  }, [isAuthenticated, authFetch, getImportedBets]);

  // Check if SharpSports browser extension is installed
  const checkBrowserExtension = useCallback(() => {
    const version = sessionStorage.getItem('sharpSportsExtensionVersion');
    setBrowserExtension((prev) => ({
      ...prev,
      isInstalled: !!version,
      version,
    }));
  }, []);

  // Get browser extension auth token (for FanDuel, DraftKings, etc.)
  const getBrowserExtensionAuthToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) return null;

    try {
      const res = await authFetch('/api/sharpsports/browser-extension-auth', {
        method: 'POST',
      });

      if (res.ok) {
        const data = (await res.json()) as { token: string; error?: string };
        if (data.error) throw new Error(data.error);
        setBrowserExtension((prev) => ({
          ...prev,
          authToken: data.token,
        }));
        return data.token;
      } else {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error || 'Failed to get browser extension auth token');
      }
    } catch (_err) {
      return null;
    }
  }, [isAuthenticated, authFetch]);

  // Reconcile bets with tracked parlays
  const reconcileBets = useCallback(async (): Promise<{
    matched: number;
    unmatched: number;
  }> => {
    if (!isAuthenticated) return { matched: 0, unmatched: 0 };

    setReconcilingBets(true);
    setError(null);

    try {
      const res = await authFetch('/api/sharpsports/reconcile', {
        method: 'POST',
      });

      if (res.ok) {
        const data = (await res.json()) as {
          matched: number;
          unmatched: number;
          error?: string;
        };
        if (data.error) throw new Error(data.error);
        return { matched: data.matched || 0, unmatched: data.unmatched || 0 };
      } else {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error || 'Failed to reconcile bets');
      }
    } catch (err) {
      setError(getUserFriendlyError(err as Error));
      return { matched: 0, unmatched: 0 };
    } finally {
      setReconcilingBets(false);
    }
  }, [isAuthenticated, authFetch]);

  // Load initial data on mount
  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      checkBrowserExtension();
      await checkStatus();
      setLoading(false);
    }
    loadInitialData();
  }, [checkStatus, checkBrowserExtension]);

  // Load accounts and bets when status shows enabled
  useEffect(() => {
    if (status?.enabled && status?.hasLinkedAccounts) {
      getLinkedAccounts();
      getImportedBets();
    }
  }, [status?.enabled, status?.hasLinkedAccounts, getLinkedAccounts, getImportedBets]);

  return {
    status,
    linkedAccounts,
    importedBets,
    browserExtension,
    loading,
    linkingInProgress,
    refreshingBets,
    processingBets,
    reconcilingBets,
    error,
    checkStatus,
    linkAccount,
    getLinkedAccounts,
    getImportedBets,
    refreshBets,
    processBets,
    reconcileBets,
    getBrowserExtensionAuthToken,
    checkBrowserExtension,
  };
}
